import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Tooltip
} from '@mui/material';
import Select, { components } from 'react-select';
import { InfoField } from 'src/containers/ClaimStatusVerify/InfoField';
import StatusTag, {
  formatDate
} from 'src/containers/Reimbursements/common/data';
import { ReimbursementShimmerLoader } from 'src/containers/Reimbursements/ReimbursementsDetails/common/Loader';
import { truncateString } from 'src/containers/Reimbursements/ReimbursementsDetails/utils/helperFunctions';

const StatusDowngradeModal = ({
  open,
  onClose,
  loading,
  formData,
  setFormData,
  claimInfo,
  claimNewStatus,
  reviewerRoles,
  finalLoading,
  handleStatusDowngrade
}) => {
  const preFilledData = {
    claimantName: claimInfo?.memberName,
    mobileNumber: claimInfo?.mobileNumber,
    claimRegisterDate: claimInfo?.claimDate,
    claimID: claimInfo?.claimId,
    subClaimID: claimInfo?.subClaimId,
    resellerName: claimInfo?.resellerName,
    loanCode: claimInfo?.loanCode,
    currentStatus: claimInfo?.status
  };

  const handleSubmit = (e) => {
    handleStatusDowngrade(claimInfo?.claimId, claimInfo?.subClaimId);
  };

  const handleChange = (field, value) => {
    if (field === 'remarks') {
      // Prevent leading spaces
      if (value.startsWith(' ')) return;

      // Prevent multiple consecutive spaces
      if (/\s{2,}/.test(value)) return;

      // Allow letters, numbers, spaces, ., , -
      const remarksRegex = /^[a-zA-Z0-9\s.,-]*$/;
      if (!remarksRegex.test(value)) return;

      // Reject if input is only numbers
      if (/^\d+$/.test(value)) return;

      // Max length 200
      if (value.length > 200) return;

      setFormData({
        ...formData,
        remarks: value
      });
      return;
    }

    if (field === 'reviewer') {
      setFormData({
        ...formData,
        reviewer: value
      });
    } else {
      setFormData({
        ...formData,
        [field]: value
      });
    }
  };

  const isRequestDisabled = () => {
    if (
      !formData?.remarks ||
      formData?.remarks.length < 15 ||
      !formData?.movableStatus ||
      !formData?.reviewer ||
      finalLoading
    ) {
      return true;
    } else {
      return false;
    }
  };

  const Placeholder = (props) => (
    <components.Placeholder {...props}>{props.children}</components.Placeholder>
  );
  return (
    <>
      <Dialog open={open} onClose={() => onClose()} fullWidth>
        <DialogTitle
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '10px 14px',
            paddingTop: '10px'
          }}
        >
          <p
            style={{
              margin: 0,
              fontSize: '18px',
              fontWeight: '500',
              color: '#2c3e50'
            }}
          >
            Reimbursement Status Transition
          </p>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '1.5rem',
              cursor: 'pointer',
              color: '#6c757d',
              borderRadius: '4px',
              transition: 'all 0.2s'
            }}
            onMouseOver={(e) => (e.target.style.color = '#dc3545')}
            onMouseOut={(e) => (e.target.style.color = '#6c757d')}
          >
            ×
          </button>
        </DialogTitle>
        <form onSubmit={handleSubmit}>
          {loading ? (
            <ReimbursementShimmerLoader />
          ) : (
            <>
              <DialogContent
                dividers
                style={{
                  padding: '14px',
                  maxHeight: '70vh', // limit height so content scrolls
                  overflowY: 'auto'
                }}
              >
                <div
                  style={{
                    backgroundColor: '#f8f9fa',
                    padding: '10px',
                    borderRadius: '8px',
                    marginBottom: '14px',
                    border: '1px solid #e1e5e9'
                  }}
                >
                  <h4
                    style={{
                      fontSize: '1.1rem',
                      fontWeight: '500',
                      color: '#495057'
                    }}
                  >
                    Reimbursement Information
                  </h4>
                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: '1fr 1fr',
                      gap: '10px'
                    }}
                  >
                    <div>
                      <InfoField
                        label="Name"
                        value={preFilledData?.claimantName}
                        loading={loading}
                      />
                      <InfoField
                        label="Mobile Number (+91)"
                        value={preFilledData?.mobileNumber}
                        loading={loading}
                      />
                      <InfoField
                        label="Register Date"
                        value={formatDate(preFilledData?.claimRegisterDate)}
                        loading={loading}
                      />
                      <InfoField
                        label="Reimbursement ID"
                        value={preFilledData?.claimID}
                        loading={loading}
                      />
                    </div>

                    {/* Right Column */}
                    <div>
                      <InfoField
                        label="Reseller Name"
                        value={preFilledData?.resellerName}
                      />
                      <InfoField
                        label="Loan Code"
                        value={preFilledData?.loanCode}
                      />
                      <InfoField
                        label="Current Status"
                        value={
                          <StatusTag status={preFilledData?.currentStatus} />
                        }
                      />
                    </div>
                  </div>
                </div>
                <div style={{ marginBottom: '24px' }}>
                  <div
                    style={{
                      marginBottom: '16px'
                    }}
                  >
                    <label
                      style={{
                        display: 'block',
                        fontWeight: '500',
                        color: '#495057',
                        fontSize: '0.9rem'
                      }}
                    >
                      Movable Status *
                    </label>
                    <div
                      style={{
                        fontSize: '0.75rem',
                        color: '#6c757d',
                        marginBottom: '4px',
                        fontStyle: 'italic'
                      }}
                    >
                      Select the status you want to move the reimbursement to.
                    </div>

                    <Select
                      isClearable
                      components={{ Placeholder }}
                      options={claimNewStatus}
                      value={claimNewStatus.find(
                        (opt) => opt.value === formData?.movableStatus
                      )}
                      onChange={(e) => handleChange('movableStatus', e?.value)}
                    />
                  </div>

                  <div style={{ marginBottom: '16px' }}>
                    <label
                      style={{
                        display: 'block',
                        fontWeight: '500',
                        color: '#495057',
                        fontSize: '0.9rem'
                      }}
                    >
                      Reviewer *
                    </label>
                    <div
                      style={{
                        fontSize: '0.75rem',
                        color: '#6c757d',
                        marginBottom: '4px',
                        fontStyle: 'italic'
                      }}
                    >
                      Select the reviewer who will be assigned with this
                      reimbursement.
                    </div>

                    <Select
                      isClearable
                      components={{ Placeholder }}
                      options={reviewerRoles}
                      value={reviewerRoles.find(
                        (opt) => opt.value === formData?.reviewer
                      )}
                      onChange={(e) => handleChange('reviewer', e?.value)}
                    />
                  </div>

                  <div style={{ marginBottom: '16px' }}>
                    <label
                      style={{
                        display: 'block',
                        fontWeight: '500',
                        color: '#495057',
                        fontSize: '0.9rem'
                      }}
                    >
                      Remarks *
                    </label>
                    <div
                      style={{
                        fontSize: '0.75rem',
                        color: '#6c757d',
                        marginBottom: '4px',
                        fontStyle: 'italic'
                      }}
                    >
                      Provide details for why you are requesting to transition
                      the reimbursement status. Minimum 15 characters.
                    </div>

                    <textarea
                      name="remarks"
                      value={formData.remarks}
                      onChange={(e) => handleChange('remarks', e.target?.value)}
                      required
                      rows="4"
                      maxLength={200}
                      minLength={15}
                      style={{
                        width: '100%',
                        padding: '10px 12px',
                        border: '1px solid #d4d6d9',
                        borderRadius: '6px',
                        fontSize: '0.9rem',
                        resize: 'vertical',
                        transition: 'border-color 0.2s',
                        boxSizing: 'border-box',
                        fontFamily: 'inherit',
                        outline: 'none'
                      }}
                      placeholder="Enter remarks for the status transition"
                    />
                  </div>
                  <div
                    style={{
                      fontSize: '0.8rem',
                      color: '#6c757d',
                      textAlign: 'right'
                    }}
                  >
                    {formData.remarks?.length || 0}/200
                  </div>
                </div>
              </DialogContent>
            </>
          )}
        </form>
        <DialogActions
          style={{
            position: 'sticky',
            bottom: 0,
            background: '#fff',
            borderTop: '1px solid #e0e0e0',
            padding: '10px 16px',
            display: 'flex',
            justifyContent: 'flex-end',
            gap: '12px'
          }}
        >
          <Button onClick={onClose}>Cancel</Button>
          <Button
            onClick={() => handleSubmit()}
            disabled={isRequestDisabled()}
            variant="contained"
          >
            {finalLoading ? <CircularProgress size={20} /> : 'Submit Request'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default StatusDowngradeModal;
